import classNames from "classnames";
import { findIndex, isNumber } from "lodash";
import DropDown from "PFComponents/dropdown/dropdown";
import InputFieldSet from "PFComponents/text/input_field_set";
import DownArrowIcon from "PFIcons/down_arrow.svg";
import PropTypes from "prop-types";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { usePopper } from "react-popper";

import css from "./select.module.scss";

const Select = forwardRef(
  (
    {
      kind,
      id,
      label,
      title,
      active,
      error,
      tip,
      locked,
      lockedTip,
      lockedTipPosition,
      disabled,
      qaId,
      style,
      displayValue,
      displayValues,
      value,
      placeholder,
      options,
      controlledValue,
      readOnly,
      handleInputChange,
      bareInput,
      whiteStyle,
      noPadding,
      rootClassName,
      inputStyle,
      inputInnerStyle,
      isDropDownAccesible,
      required,
      isFull,
      closeOnChange,
      handleChange,
      handleOpen,
      handleClose,
      handleDropDownToggle,
      preOptions,
      multi,
      portalRef,
      popperOptions,
      icon,
      classes,
      inputFieldSetProps,
      ...dropdownProps
    },
    ref
  ) => {
    const [display, setDisplay] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(dropdownProps.selectedIndex);

    const popperReferenceElement = useRef();
    const inputRef = useRef();
    const dropDownRef = useRef();

    if (ref?.current) {
      ref.current.inputRef = inputRef;
    }

    useEffect(() => {
      if (controlledValue) {
        setSelectedIndex(dropdownProps.selectedIndex);
      }
    }, [controlledValue, dropdownProps.selectedIndex]);

    const isDropdownShown = (display) => display && !isFull;

    const handleChangeFnc = (item, option) => {
      const newSelectedIndex = findIndex(options, (option) => option.item === item);

      setSelectedIndex(newSelectedIndex);

      if (closeOnChange) {
        handleCloseFnc();
      } else {
        updatePopper && updatePopper();
      }

      handleChange && handleChange(item, option);
    };

    const handleOpenFnc = () => {
      if (display) {
        return;
      }
      setDisplay(true);
      handleDropDownToggle && handleDropDownToggle(isDropdownShown(true));
      handleOpen && handleOpen();
    };

    const handleBlur = (event) => {
      if (
        (event.relatedTarget && event.relatedTarget.hasAttribute("data-dropdown-root")) ||
        (document.activeElement && document.activeElement.hasAttribute("data-dropdown-root"))
      ) {
        if (inputRef.current) {
          try {
            inputRef.current.focus();
          } catch {}
        }

        return;
      }
      handleCloseFnc();
    };

    const handleCloseFnc = () => {
      setDisplay(false);
      if (inputRef.current) {
        try {
          inputRef.current.blur();
        } catch {}
      }
      handleDropDownToggle && handleDropDownToggle(isDropdownShown(false));
      handleClose && handleClose();
    };

    const handleKeyDown = (event) => dropDownRef.current && dropDownRef.current.handleKeyDown(event);
    const handleKeyUp = (event) => dropDownRef.current && dropDownRef.current.handleKeyUp(event);

    let inputValue;

    if (controlledValue) {
      inputValue = value;
    } else {
      inputValue = isNumber(selectedIndex) ? options[selectedIndex].displayElement : "";
    }

    const [popperElement, setPopperElement] = useState(null);
    const {
      styles,
      attributes,
      update: updatePopper
    } = usePopper(popperReferenceElement.current, popperElement, {
      placement: "bottom-start",
      ...popperOptions
    });

    const Dropdown = (
      <div ref={setPopperElement} style={{ ...styles.popper, zIndex: 20000 }} {...attributes.popper}>
        <DropDown
          {...dropdownProps}
          options={options}
          ref={dropDownRef}
          style={{ maxWidth: popperReferenceElement.current?.offsetWidth, ...dropdownProps.dropdownStyle }}
          selectedIndex={selectedIndex}
          preOptions={preOptions}
          handleChange={handleChangeFnc}
          handleClose={handleCloseFnc}
          isAccesible={isDropDownAccesible}
          multi={multi}
        />
      </div>
    );

    return (
      <div
        ref={ref}
        className={classNames(css.select, rootClassName, classes.root)}
        style={style}
        data-qa-id={qaId}
      >
        <div ref={popperReferenceElement}>
          <InputFieldSet
            kind={kind}
            id={id}
            placeholder={placeholder}
            inputTagRef={inputRef}
            label={label}
            title={title}
            active={active}
            noPadding={noPadding}
            error={error}
            tip={tip}
            locked={locked}
            lockedTip={lockedTip}
            lockedTipPosition={lockedTipPosition}
            disabled={disabled}
            bareInput={bareInput}
            value={inputValue}
            displayValue={displayValue}
            displayValues={displayValues}
            controlledValue={controlledValue}
            readOnly={readOnly}
            whiteStyle={whiteStyle}
            handleChange={handleInputChange}
            handleKeyDown={handleKeyDown}
            handleKeyUp={handleKeyUp}
            handleFocus={handleOpenFnc}
            handleBlur={handleBlur}
            icon={icon || (bareInput ? null : <DownArrowIcon width={20} height={20} />)}
            inputStyle={inputStyle}
            innerStyle={inputInnerStyle}
            required={required}
            classes={classes.inputField}
            {...inputFieldSetProps}
          />
        </div>
        {isDropdownShown(display) &&
          !disabled &&
          (portalRef?.current ? ReactDOM.createPortal(Dropdown, portalRef.current) : Dropdown)}
      </div>
    );
  }
);

Select.displayName = "Select";

Select.propTypes = {
  ...InputFieldSet.propTypes,
  ...DropDown.propTypes,

  bareInput: PropTypes.bool,

  qaId: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  controlledValue: PropTypes.bool,
  displayValue: PropTypes.node,
  displayValues: PropTypes.node,
  closeOnChange: PropTypes.bool,
  isFull: PropTypes.bool,
  isDropDownAccesible: PropTypes.bool,

  handleInputChange: PropTypes.func,
  handleKeyDown: PropTypes.func,
  handleOpen: PropTypes.func,
  handleClose: PropTypes.func,
  handleChange: PropTypes.func,
  required: PropTypes.bool,
  multi: PropTypes.bool,
  dropdownStyle: PropTypes.object,
  classes: PropTypes.shape({
    inputField: InputFieldSet.propTypes.classes,
    root: InputFieldSet.propTypes.classes
  }),
  inputFieldSetProps: PropTypes.object
};

Select.defaultProps = {
  ...InputFieldSet.defaultProps,
  ...DropDown.defaultProps,

  displayValue: null,
  displayValues: null,
  closeOnChange: true,

  handleInputChange: null,
  handleKeyDown: null,
  handleOpen: null,
  handleClose: null,
  handleChange: null,

  bareInput: false,
  dropdownStyle: {},
  classes: {
    inputField: {}
  },
  inputFieldSetProps: {}
};

export default Select;
